import React, { useCallback, useMemo, useState } from 'react';
import styles from './productListOverview.module.scss';
import { Product, ProductAvailability } from 'core/product/Product';
import { partial } from 'lodash';
import i18n from 'i18n';
import { Slider } from 'components/Slider/Slider';
import { DefaultSliderModel } from 'components/Slider/SliderModel';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';

const cx = classNames.bind(styles);

export const ProductListOverview: React.FC<React.PropsWithChildren<{
  products: Product[],
  handleRemoveProduct?: (productId: string) => void
  handleRemoveAll?: () => void
  /** 商品縮圖區一開始要不要展開。商品列表頁維持展開；關鍵字活動的設定頁空間很擠，預設收起來。 */
  defaultOpen?: boolean
}>> = ({
  products,
  handleRemoveProduct,
  handleRemoveAll,
  defaultOpen = true,
  children
}) => {

  const [openOverviewArea, setOpenOverviewArea] = useState<boolean>(defaultOpen);

  const overview = products.sort((a, b) => {
    if (a.availability === ProductAvailability.OUT_OF_STOCK && b.availability !== ProductAvailability.OUT_OF_STOCK) {
      return -1;
    } else if (a.availability !== ProductAvailability.OUT_OF_STOCK && b.availability === ProductAvailability.OUT_OF_STOCK) {
      return 1;
    } else {
      return 0;
    }
  }).map(product => {
    let productName = product.name;
    if (product.availability === ProductAvailability.OUT_OF_STOCK) {
      productName = `(${i18n.t<string>('productListOverview.labels.outOfStock')}) ${product.name}`;
    }
    return (
      <div key={product.productId} className={styles.overview}>
        <div className={styles.overviewLeft}>
          <div className={styles.checkbox}>
            {handleRemoveProduct &&
              <input
                type='checkbox'
                checked={true}
                onChange={partial(handleRemoveProduct, product.productId)}
                id={product.productId}
              />
            }
            <label htmlFor={product.productId} />
          </div>
          <img src={product.imgLink} alt={product.name} />
        </div>
        <div className={styles.overviewRight}>
          <div className={styles.productName}>{productName}</div>
          <div className={styles.productId}>{`ID: ${product.productId}`}</div>
        </div>
      </div>
    );
  });

  const triggerOverviewArea = useCallback(() => {
    setOpenOverviewArea(!openOverviewArea);
  }, [openOverviewArea]);

  const sliderModel = useMemo(() => new DefaultSliderModel(195, 92, false, false, 0, false), []);
  const handleRemoveAllAndClose = useCallback(() => {
    handleRemoveAll && handleRemoveAll();
    sliderModel.handleSelect(0);
  }, [handleRemoveAll, sliderModel]);

  const overviewAreaClass = cx(styles.overviewArea, {
    open: openOverviewArea
  });

  return (
    <div className={styles.productListOverview}>
      <div className={styles.operateArea}>
        <div className={styles.selectedInfo}>
          {i18n.t<string>('productListOverview.labels.selectCount', {
            count: products.length
          })}
        </div>
        {children}
        <div
          className={styles.toggleOverview}
          onClick={triggerOverviewArea}
        >
          <FontAwesomeIcon icon={openOverviewArea ? faAngleDown : faAngleUp} />
        </div>
        {handleRemoveAll && (
          <div className={styles.removeAll} onClick={handleRemoveAllAndClose}>
            {i18n.t<string>('productListOverview.labels.removeAll')}
          </div>
        )}
      </div>
      <div className={styles.seperationLine} />
      <div className={overviewAreaClass}>
        <Slider
          prevIcon={<div className={styles.arrowLeft} />}
          nextIcon={<div className={styles.arrowRight} />}
          prevIconClassName={styles.arrowLeftContainer}
          nextIconClassName={styles.arrowRightContainer}
          fill={true}
          noDataIndication={
            overview.length === 0
              ? i18n.t<string>('productListOverview.placeholders.noProduct')
              : undefined
          }
          model={sliderModel}
        >
          {overview}
        </Slider>
      </div>
    </div>
  );
};
